import {
  StorefrontTreatmentId,
  storefrontTreatmentIdFormat,
} from "@redotech/redo-coverage-id";

const OBJECT_ID_HEX_PATTERN = /^[0-9a-f]{24}$/i;
const INT8_DECIMAL_PATTERN = /^(0|[1-9]\d{0,18})$/;
const INT8_MAX = 9_223_372_036_854_775_807n;

/** Which id space a coverage treatment id belongs to. */
export enum CoverageTreatmentIdSource {
  MONGO_TREATMENT = "mongo_treatment",
  STOREFRONT_TREATMENT = "storefront_treatment",
}

/** Always set together: an id is meaningless without its id space. */
export interface CoverageTreatmentAttribution {
  coverageTreatmentId: string;
  coverageTreatmentIdSource: CoverageTreatmentIdSource;
}

function isObjectIdHex(candidate: string): boolean {
  return OBJECT_ID_HEX_PATTERN.test(candidate);
}

function isInt8Decimal(candidate: string): boolean {
  return INT8_DECIMAL_PATTERN.test(candidate) && BigInt(candidate) <= INT8_MAX;
}

const ID_FORMAT_CHECK_BY_SOURCE: Record<
  CoverageTreatmentIdSource,
  (candidate: string) => boolean
> = {
  [CoverageTreatmentIdSource.MONGO_TREATMENT]: isObjectIdHex,
  [CoverageTreatmentIdSource.STOREFRONT_TREATMENT]: isInt8Decimal,
};

function isCoverageTreatmentIdSource(
  candidate: unknown,
): candidate is CoverageTreatmentIdSource {
  return Object.values<unknown>(CoverageTreatmentIdSource).includes(candidate);
}

/**
 * Keeps a coverage A/B treatment attribution only if the id's format matches
 * its source (Mongo ObjectId hex for `mongo_treatment`, decimal int8 for
 * `storefront_treatment`); anything else yields undefined so the caller drops
 * both fields and still keeps the customer event. The id is returned as the
 * original string, because int8 ids exceed `Number.MAX_SAFE_INTEGER`.
 *
 * Does not import `isObjectIdString` from redo-conversion-core-common: that
 * package depends on this one.
 */
export function parseCoverageTreatmentAttribution(
  rawCoverageTreatmentId: unknown,
  rawCoverageTreatmentIdSource: unknown,
): CoverageTreatmentAttribution | undefined {
  if (
    typeof rawCoverageTreatmentId !== "string" ||
    !isCoverageTreatmentIdSource(rawCoverageTreatmentIdSource) ||
    !ID_FORMAT_CHECK_BY_SOURCE[rawCoverageTreatmentIdSource](
      rawCoverageTreatmentId,
    )
  ) {
    return undefined;
  }
  return {
    coverageTreatmentId: rawCoverageTreatmentId,
    coverageTreatmentIdSource: rawCoverageTreatmentIdSource,
  };
}

/**
 * Attribution for producers that read the id from the Mongo-backed runtime:
 * only a Mongo ObjectId is attributed, anything else yields nothing.
 */
export function coverageAttributionFromMongoTreatmentId(
  rawMongoTreatmentId: unknown,
): CoverageTreatmentAttribution | undefined {
  return parseCoverageTreatmentAttribution(
    rawMongoTreatmentId,
    CoverageTreatmentIdSource.MONGO_TREATMENT,
  );
}

/**
 * Attribution for the `redo_treatment_id` the storefront stores: a Mongo
 * ObjectId, or a storefront A/B arm id (`strt_...`) recorded as its int8.
 * The value is a cart attribute anyone can set, so a malformed arm id yields
 * nothing instead of throwing.
 */
export function coverageAttributionFromTreatmentId(
  rawTreatmentId: unknown,
): CoverageTreatmentAttribution | undefined {
  if (
    typeof rawTreatmentId === "string" &&
    storefrontTreatmentIdFormat.test(rawTreatmentId)
  ) {
    const int8Id = readStorefrontTreatmentInt8(rawTreatmentId);
    return int8Id === undefined
      ? undefined
      : parseCoverageTreatmentAttribution(
          int8Id,
          CoverageTreatmentIdSource.STOREFRONT_TREATMENT,
        );
  }
  return coverageAttributionFromMongoTreatmentId(rawTreatmentId);
}

function readStorefrontTreatmentInt8(
  storefrontTreatmentId: StorefrontTreatmentId,
): string | undefined {
  try {
    return storefrontTreatmentIdFormat.read(storefrontTreatmentId).toString();
  } catch {
    return undefined;
  }
}

/** Whether a stored `redo_treatment_id` names a legacy Mongo treatment. */
export function isMongoCoverageTreatmentId(
  rawTreatmentId: unknown,
): rawTreatmentId is string {
  return typeof rawTreatmentId === "string" && isObjectIdHex(rawTreatmentId);
}
